import {MessarioAiBundle, MessarioAiSession} from '@lib/messarioAi/types';

export const MESSARIO_AI_SESSION_KEY_PREFIX = 'messario_ai_session:';

function storageKey(externalUserId: string) {
  return `${MESSARIO_AI_SESSION_KEY_PREFIX}${externalUserId}`;
}

export function isAiBundle(value: unknown): value is MessarioAiBundle {
  if(!value || typeof value !== 'object') return false;
  const bundle = value as MessarioAiBundle;
  if(bundle.config_schema_version !== 1) return false;
  if(bundle.enabled === false) return true;
  return bundle.enabled === true && bundle.published === true &&
    typeof bundle.prompt_revision === 'number' && typeof bundle.variable_definitions_revision === 'number' &&
    typeof bundle.config_hash === 'string' && typeof bundle.model === 'string' &&
    typeof bundle.history_limit === 'number' && typeof bundle.max_output_tokens === 'number' &&
    typeof bundle.timezone === 'string' && !!bundle.features &&
    typeof bundle.features.one_shot_instruction === 'boolean' && typeof bundle.features.draft_only === 'boolean';
}

export function bundleMetadata(bundle: MessarioAiBundle): MessarioAiBundle {
  if(!bundle.enabled) return {enabled: false, config_schema_version: 1, published: bundle.published};
  return {
    enabled: true, published: true, config_schema_version: 1,
    prompt_revision: bundle.prompt_revision, variable_definitions_revision: bundle.variable_definitions_revision,
    config_hash: bundle.config_hash, published_at: bundle.published_at, model: bundle.model,
    history_limit: bundle.history_limit, max_output_tokens: bundle.max_output_tokens, timezone: bundle.timezone,
    folder_id: bundle.folder_id,
    features: {one_shot_instruction: bundle.features.one_shot_instruction, draft_only: bundle.features.draft_only}
  };
}

function isSession(value: unknown): value is MessarioAiSession {
  if(!value || typeof value !== 'object') {
    return false;
  }

  const session = value as MessarioAiSession;
  return typeof session.account_id === 'string' &&
    !!session.account_id &&
    typeof session.panel_user_id === 'string' &&
    !!session.panel_user_id &&
    typeof session.external_user_id === 'string' &&
    !!session.external_user_id &&
    isAiBundle(session.ai_bundle);
}

function metadataOnlySession(session: MessarioAiSession): MessarioAiSession {
  return {account_id: session.account_id, panel_user_id: session.panel_user_id,
    external_user_id: session.external_user_id, ai_bundle: bundleMetadata(session.ai_bundle)};
}

export function saveMessarioAiSession(
  session: MessarioAiSession,
  storage: Storage = window.sessionStorage
) {
  if(!session.external_user_id || !isSession(session)) {
    return;
  }

  storage.setItem(storageKey(session.external_user_id), JSON.stringify(metadataOnlySession(session)));
}

export function clearMessarioAiSession(
  externalUserId: string,
  storage: Storage = window.sessionStorage
) {
  storage.removeItem(storageKey(externalUserId));
}

export function getMessarioAiSession(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiSession | undefined {
  const value = storage.getItem(storageKey(externalUserId));
  if(!value) {
    return undefined;
  }

  try {
    const session = JSON.parse(value) as unknown;
    if(!isSession(session) || session.external_user_id !== externalUserId) {
      clearMessarioAiSession(externalUserId, storage);
      return undefined;
    }

    return session;
  } catch(err) {
    clearMessarioAiSession(externalUserId, storage);
    return undefined;
  }
}

export function getMessarioAiBundle(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): MessarioAiBundle | undefined {
  return getMessarioAiSession(externalUserId, storage)?.ai_bundle;
}

export function isMessarioAiEnabledForUser(
  externalUserId: string,
  storage: Storage = window.sessionStorage
): boolean {
  const bundle = getMessarioAiBundle(externalUserId, storage);
  return !!bundle?.enabled;
}
